import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ReportData } from '@/types/report';
import { useAuth } from '@/context/auth/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EditReportForm, formSchema } from './edit-report/EditReportForm';
import * as z from 'zod';

interface EditReportDialogProps {
  report: ReportData | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditReportDialog = ({
  report,
  isOpen,
  onClose,
  onSuccess,
}: EditReportDialogProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!report || !user) return;

    try {
      setLoading(true);

      const fieldsToUpdate: Record<string, any> = {};
      const fieldsChanged: { field: string; oldValue: any; newValue: any }[] = [];

      if (values.status !== report.status) {
        fieldsToUpdate.status = values.status;
        fieldsChanged.push({
          field: 'status',
          oldValue: report.status,
          newValue: values.status,
        });
      }

      if (values.learning_status !== report.learning_status) {
        fieldsToUpdate.learning_status = values.learning_status;
        fieldsChanged.push({
          field: 'learning_status',
          oldValue: report.learning_status,
          newValue: values.learning_status,
        });
      }

      if (values.submitted_on_time !== report.submitted_on_time) {
        fieldsToUpdate.submitted_on_time = values.submitted_on_time;
        fieldsChanged.push({
          field: 'submitted_on_time',
          oldValue: report.submitted_on_time ? 'true' : 'false',
          newValue: values.submitted_on_time ? 'true' : 'false',
        });
      }

      if (values.class_duration !== report.class_duration) {
        fieldsToUpdate.class_duration = values.class_duration;
        fieldsChanged.push({
          field: 'class_duration',
          oldValue: report.class_duration?.toString() || 'null',
          newValue: values.class_duration?.toString() || 'null',
        });
      }

      if (values.teaching_duration !== report.teaching_duration) {
        fieldsToUpdate.teaching_duration = values.teaching_duration;
        fieldsChanged.push({
          field: 'teaching_duration',
          oldValue: report.teaching_duration,
          newValue: values.teaching_duration,
        });
      }

      if (values.teaching_type !== report.teaching_type) {
        fieldsToUpdate.teaching_type = values.teaching_type;
        fieldsChanged.push({
          field: 'teaching_type',
          oldValue: report.teaching_type || 'null',
          newValue: values.teaching_type || 'null',
        });
      }

      if (values.status === 'REJECTED') {
        if (values.rejection_reason !== report.rejection_reason) {
          fieldsToUpdate.rejection_reason = values.rejection_reason;
          fieldsChanged.push({
            field: 'rejection_reason',
            oldValue: report.rejection_reason || 'null',
            newValue: values.rejection_reason || 'null',
          });
        }
      } else if (report.rejection_reason) {
        fieldsToUpdate.rejection_reason = null;
        fieldsChanged.push({
          field: 'rejection_reason',
          oldValue: report.rejection_reason,
          newValue: 'null',
        });
      }

      if (Object.keys(fieldsToUpdate).length === 0) {
        toast({
          title: 'ไม่มีการเปลี่ยนแปลง',
          description: 'ไม่มีข้อมูลที่ถูกเปลี่ยนแปลง',
        });
        onClose();
        return;
      }

      const { error: updateError } = await supabase
        .from('student_development_reports')
        .update(fieldsToUpdate)
        .eq('id', report.id);

      if (updateError) {
        throw updateError;
      }

      for (const change of fieldsChanged) {
        const { error: logError } = await supabase
          .from('report_update_logs' as any)
          .insert({
            report_id: report.id,
            admin_id: user.id,
            admin_email: user.email || '',
            field_changed: change.field,
            old_value: change.oldValue,
            new_value: change.newValue,
            reason: values.reason,
          } as any);

        if (logError) {
          console.error('Error logging change:', logError);
        }
      }

      toast({
        title: 'อัพเดทรายงานสำเร็จ',
        description: 'ข้อมูลรายงานถูกอัพเดทเรียบร้อยแล้ว',
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating report:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัพเดทข้อมูลรายงานได้',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>แก้ไขรายงาน</DialogTitle>
          <DialogDescription>
            แก้ไขข้อมูลรายงานของ {report?.student_nickname} ({report?.student_full_name})
          </DialogDescription>
        </DialogHeader>

        <EditReportForm
          report={report}
          onSubmit={handleSubmit}
          loading={loading}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};
